/**
 * RelTime.jsx — Relative time label with themed datetime tooltip on hover.
 *
 * Issue #121. Wraps the existing `relTime()` formatter from lib/format.js
 * in a span that shows the exact local datetime via the v2 mouse-following
 * Tooltip primitive (#120). Use anywhere we previously rendered
 * `{relTime(ts)}` directly — LiveFeed, FissalAI sessions, MySales, Guild/
 * Player Tracking, etc.
 *
 * Usage:
 *   <RelTime ts={row.timestamp} />
 *   <RelTime ts={row.timestamp} class="v2-sale-row__time" />
 *
 * Props:
 *   - ts        : numeric timestamp (seconds or ms; tsMs handles both).
 *   - class     : optional extra class (the wrapper still gets v2-rel-time).
 *   - empty     : string to show when ts is falsy. Defaults to ''.
 */

import { tipProps } from './MouseTooltip.jsx';
import { relTime, tsMs } from '../lib/format.js';

function fullDateTime(ts) {
  const ms = tsMs(ts);
  if (!ms) return '';
  const d = new Date(ms);
  if (isNaN(d.getTime())) return '';
  // Locale-aware long datetime, e.g. "Apr 30, 2026, 8:45:37 AM" — exact
  // wall-clock value mirrors what the underlying timestamp encodes.
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

export default function RelTime(props) {
  const cls = () => 'v2-rel-time' + (props.class ? ' ' + props.class : '');
  const label = () => (props.ts ? relTime(props.ts) : (props.empty ?? ''));
  const tip = () => fullDateTime(props.ts);

  return (
    <span class={cls()} {...(tip() ? tipProps(tip()) : {})}>
      {label()}
    </span>
  );
}
